import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Integrators, IntegratorsLabel } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Checkbox } from "@/components/Base/Checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Plus } from "lucide-react";
import { useAdminCreateShopMutation } from "@/hooks/mutations/useAdminCreateShopMutation";
import { useToast } from "@/hooks/useToast";

const formSchema = z.object({
  email: z.string().email("Enter a valid email"),
  shopName: z.string().min(1, "Shop name is required"),
  integrator: z.nativeEnum(Integrators),
  sendActivationEmailNow: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateShopDialog = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync, isLoading } = useAdminCreateShopMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      shopName: "",
      integrator: Integrators.manual,
      sendActivationEmailNow: true,
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await mutateAsync({
        email: values.email,
        shopName: values.shopName,
        integrator: values.integrator,
        sendActivationEmailNow: values.sendActivationEmailNow,
        loginLink: `${window.location.origin}/login`,
      });

      toast({
        title: "Shop created",
        description: result.emailSent
          ? "The owner's login email was sent."
          : "The owner's login email was held — send it from the shop's page when you're ready.",
        variant: "success",
      });

      form.reset();
      setOpen(false);
      navigate(result.shop.id);
    } catch {
      toast({
        title: "Unable to create shop",
        description: "Try again in a few minutes or contact support.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button variant="default" onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Shop
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Create Shop</DialogTitle>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="ownerEmail">Owner Email</FormLabel>
                  <FormControl>
                    <Input
                      id="ownerEmail"
                      type="email"
                      placeholder="owner@example.com"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shopName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="shopName">Shop Name</FormLabel>
                  <FormControl>
                    <Input
                      id="shopName"
                      type="text"
                      placeholder="Joe's Auto"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="integrator"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Integration</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an integration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Integrators.shopware}>
                          {IntegratorsLabel[Integrators.shopware]}
                        </SelectItem>
                        <SelectItem value={Integrators.tekmetric}>
                          {IntegratorsLabel[Integrators.tekmetric]}
                        </SelectItem>
                        <SelectItem value={Integrators.manual}>
                          {IntegratorsLabel[Integrators.manual]}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sendActivationEmailNow"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onClick={() => field.onChange(!field.value)}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormLabel>Send login email now</FormLabel>
                </FormItem>
              )}
            />
            <div className="flex flex-row justify-end gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="accent" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Shop"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
